import { Browser } from '@capacitor/browser'
import { supabase } from '../supabase'
import {
  fetchOfferings, isNativeIAPPlatform, openNativeSubscriptionManagement,
  purchasePackage,
} from './iap'

export type BillingPlan = 'monthly' | 'yearly'

/**
 * Asks the create-checkout-session edge function for a Stripe
 * Checkout URL and navigates the user to it. After Stripe collects
 * payment details + starts the 3-day trial, the webhook hits
 * `email_subscriptions` and the user lands back on the app with
 * `?subscribed=1` in the URL.
 */
export async function startCheckout(plan: BillingPlan): Promise<void> {
  const { data, error } = await supabase.functions.invoke('create-checkout-session', {
    body: { plan, return_to: window.location.origin },
  })
  if (error) throw error
  const url = (data as { url?: string })?.url
  if (!url) throw new Error('No checkout URL returned by Stripe.')
  window.location.assign(url)
}

/**
 * Opens the Stripe Customer Portal so the user can cancel, update
 * their card, change plan, etc. Returns null if no Stripe customer
 * exists yet (the caller should show the Subscribe CTA instead).
 *
 * On native the app's origin is capacitor://localhost — useless as a
 * Stripe return URL — so we omit return_to (the edge function falls
 * back to APP_URL) and open the portal in the system browser instead
 * of navigating the webview away from the app.
 */
export async function openCustomerPortal(): Promise<void> {
  const native = isNativeIAPPlatform()
  const { data, error } = await supabase.functions.invoke('create-portal-session', {
    body: native ? {} : { return_to: window.location.origin },
  })
  if (error) throw error
  const url = (data as { url?: string })?.url
  if (!url) throw new Error('No portal URL returned by Stripe.')
  // WKWebView silently drops window.open() calls that arrive after an
  // async boundary (no user-gesture context), so on native we go through
  // the Browser plugin, which opens Safari from any JS context.
  if (native) await Browser.open({ url })
  else window.location.assign(url)
}

/**
 * Platform-aware subscribe entry point. On web, kicks off the
 * existing Stripe Checkout. On iOS / Android, fetches the matching
 * RevenueCat package and triggers the native purchase sheet.
 *
 * Apple's guideline 3.1.1 forbids linking out to a web payment for
 * digital subscriptions, so on native we MUST hit StoreKit (via
 * RevenueCat). Same rule on Google Play. Web is the only place a
 * Stripe URL is allowed.
 */
export async function subscribe(plan: BillingPlan): Promise<void> {
  if (isNativeIAPPlatform()) {
    const offerings = await fetchOfferings()
    const match = offerings.find(p => p.plan === plan)
    if (!match) {
      throw new Error('Subscription products are still being set up. Try again later.')
    }
    await purchasePackage(match.identifier)
    return
  }
  await startCheckout(plan)
}

/**
 * "Manage subscription" entry point. Routes by where the subscription
 * actually LIVES, not by platform — a Stripe subscriber on an iPhone
 * must reach the Stripe portal (Apple's subscription settings know
 * nothing about their sub), and an Apple subscriber on the web gets
 * pointed at Apple's manage-subscriptions page.
 *
 * Pass useSubscription().source. Unknown/legacy sources fall back to
 * the old platform-based behavior. 'stamps' routes to the website
 * account page — stamp time is granted there, so that is the only
 * place it can be looked after (owner call 2026-08-11; Apple/Stripe
 * know nothing about it and would dead-end).
 */
export async function manageSubscription(
  source?: 'apple' | 'squarespace' | 'stripe' | 'manual' | 'stamps' | null,
): Promise<void> {
  if (source === 'stamps') {
    await Browser.open({ url: 'https://jordandraper.com/account' })
    return
  }
  if (source === 'stripe') {
    await openCustomerPortal()
    return
  }
  if (source === 'apple') {
    if (isNativeIAPPlatform()) await openNativeSubscriptionManagement()
    else await Browser.open({ url: 'https://apps.apple.com/account/subscriptions' })
    return
  }
  // squarespace / manual / unknown: keep the platform default.
  if (isNativeIAPPlatform()) {
    await openNativeSubscriptionManagement()
    return
  }
  await openCustomerPortal()
}
